/**
 * Filtering logic for the group booking system.
 */

import { BOOKING_OPTIONS, SWIMMING_OPTIONS } from "./formatting.js";

/**
 * Filters bookings by meeting point.
 * Returns only bookings whose meeting_point_id matches the given meetingPointId.
 *
 * @param {Array} bookings - Array of booking objects
 * @param {number|string} meetingPointId - The meeting point ID to filter by
 * @returns {Array} Bookings matching the selected meeting point
 */
export function filterByMeetingPoint(bookings, meetingPointId) {
  if (!Array.isArray(bookings) || meetingPointId === null || meetingPointId === undefined) {
    return [];
  }
  const id = Number(meetingPointId);
  return bookings.filter(
    (booking) => booking && Number(booking.meeting_point_id) === id
  );
}

/**
 * Filters bookings by selected options using AND logic.
 * Returns only bookings that contain ALL selected options.
 *
 * Each booking has an `options` array of `{label, quantity}` objects.
 * selectedOptions is an array of option value strings (e.g., ["swimming", "no_camel"]).
 * A booking matches if its options array contains entries whose labels match
 * all selected options (case-insensitive comparison using the BOOKING_OPTIONS mapping).
 *
 * @param {Array} bookings - Array of booking objects
 * @param {Array<string>} selectedOptions - Array of option value strings to filter by
 * @returns {Array} Bookings containing ALL selected options
 */
export function filterByOptions(bookings, selectedOptions) {
  if (!Array.isArray(bookings) || !Array.isArray(selectedOptions) || selectedOptions.length === 0) {
    return Array.isArray(bookings) ? bookings : [];
  }

  // Build a map from option value to label for case-insensitive comparison
  const valuesToLabels = {};
  for (const opt of BOOKING_OPTIONS) {
    valuesToLabels[opt.value.toLowerCase()] = opt.label.toLowerCase();
  }

  // Resolve selected option values to their expected labels (lowercase)
  const requiredLabels = selectedOptions.map(
    (value) => valuesToLabels[value.toLowerCase()] || value.toLowerCase()
  );

  return bookings.filter((booking) => {
    if (!booking || !Array.isArray(booking.options)) {
      return false;
    }
    // Get all labels from the booking's options (lowercase)
    const bookingLabels = booking.options.map(
      (opt) => (opt.label || "").toLowerCase()
    );
    // Check that ALL required labels are present in the booking
    return requiredLabels.every((label) => bookingLabels.includes(label));
  });
}

/**
 * Filters bookings to only those with swimming-related options.
 * Returns bookings that have "Swimming" or "Ticket Swimming" options.
 *
 * @param {Array} bookings - Array of booking objects
 * @returns {Array} Bookings with swimming-related options
 */
export function filterSwimmingBookings(bookings) {
  if (!Array.isArray(bookings)) {
    return [];
  }

  // Get swimming labels from BOOKING_OPTIONS for case-insensitive matching
  const swimmingLabels = BOOKING_OPTIONS
    .filter((opt) => SWIMMING_OPTIONS.includes(opt.value))
    .map((opt) => opt.label.toLowerCase());

  return bookings.filter((booking) => {
    if (!booking || !Array.isArray(booking.options)) {
      return false;
    }
    return booking.options.some(
      (opt) => swimmingLabels.includes((opt.label || "").toLowerCase())
    );
  });
}

/**
 * Counts the total number of participants across all bookings in the array.
 *
 * @param {Array} bookings - Array of booking objects
 * @returns {number} Sum of participants across all bookings
 */
export function countParticipants(bookings) {
  if (!Array.isArray(bookings)) {
    return 0;
  }
  return bookings.reduce((sum, booking) => {
    if (!booking) return sum;
    const participants = parseInt(booking.participants, 10);
    return sum + (isNaN(participants) ? 0 : participants);
  }, 0);
}
